import { useState, useEffect, useRef } from 'react';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, Heart, User, Bookmark, BookmarkCheck, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/chat/history`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(`${API}/chat/message`, { message: userMessage });
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempUserMsg.id),
        response.data.user_message,
        response.data.assistant_message
      ]);
    } catch (error) {
      toast.error('Could not send message.');
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      setInput(userMessage);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const saveMessage = async (messageId) => {
    try {
      await axios.post(`${API}/chat/save/${messageId}`);
      setMessages(prev => 
        prev.map(m => m.id === messageId ? { ...m, saved: true } : m)
      );
      toast.success('Saved!');
    } catch (error) {
      toast.error('Could not save');
    }
  };

  const suggestedPrompts = [
    "I need peace today",
    "Pray for my family",
    "Help me forgive",
    "Give me strength",
    "I feel anxious",
    "Thank God with me",
    "I need wisdom",
    "Comfort my heart"
  ];

  return (
    <div className="min-h-screen sky-gradient-light flex flex-col pb-safe" data-testid="chat-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden">
            <img src="/images/logo.png" alt="BlessBe" className="w-12 h-12 object-cover" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Prayer Companion</h1>
            <p className="text-sm text-gray-500">I&apos;m here for you</p>
          </div>
        </div>
      </header>

      {/* Messages */}
      <ScrollArea className="flex-1 px-6" ref={scrollRef}>
        <div className="py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-10 h-10 text-blue-500" />
              </div>
              <h2 className="text-lg font-bold text-gray-800 mb-2">How can I pray for you?</h2>
              <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
                Share what&apos;s on your heart. I&apos;ll pray with you and share encouraging Scripture.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {suggestedPrompts.map((prompt, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                    onClick={() => setInput(prompt)}
                    data-testid={`suggested-prompt-${idx}`}
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.role}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img src="/images/logo.png" alt="BlessBe" className="w-8 h-8 object-cover" />
                  </div>
                )}
                <div className={`max-w-[80%] ${message.role === 'user' ? 'order-first' : ''}`}>
                  <Card className={`rounded-2xl border-0 shadow-sm ${
                    message.role === 'user' 
                      ? 'bg-blue-500 text-white rounded-tr-sm' 
                      : 'bg-white rounded-tl-sm'
                  }`}>
                    <div className="p-3">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                  </Card>
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mt-1 ml-1">
                      <button
                        onClick={() => !message.saved && saveMessage(message.id)}
                        className="text-gray-400 hover:text-blue-500 transition-colors"
                        disabled={message.saved}
                        data-testid={`save-message-${message.id}`}
                      >
                        {message.saved ? (
                          <BookmarkCheck className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Bookmark className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                )}
              </div>
            ))
          )}
          {sending && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Heart className="w-4 h-4 text-blue-500 fill-blue-500 animate-pulse" />
              </div>
              <Card className="rounded-2xl bg-white rounded-tl-sm border-0 shadow-sm">
                <div className="p-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="px-6 py-4 bg-white/80 backdrop-blur-lg border-t border-gray-100 mb-16">
        <form onSubmit={sendMessage} className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Write a prayer or ask for help..."
            className="flex-1 h-12 rounded-full bg-gray-50 border-gray-200 px-5"
            disabled={sending}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            size="icon"
            className="h-12 w-12 rounded-full bg-blue-500 hover:bg-blue-600 shadow-md"
            disabled={!input.trim() || sending}
            data-testid="send-button"
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>

      <BottomNav />
    </div>
  );
}
